import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack, Button, IconButton, Tooltip } from '@mui/material';
import { AddRounded, EditRounded, DeleteRounded, PersonOffRounded } from '@mui/icons-material';
import { useUsers, useRoles } from '@/queries/users';
import { useSpoofUser } from '@/queries/auth';
import Backdrop from '@/components/ui/backdrop';
import { useQueryClient } from '@tanstack/react-query';
import { UserFormModel } from '@/features/users/forms/user-form/types';
import UserForm from '@/features/users/forms/user-form/UserForm';
import { CustomerUser } from '@/core/entities';
import {
    initialUserFormModel,
    userFormModelFromUser,
} from '@/features/users/forms/user-form/user-form.defaults';
import DeleteUserForm from '@/features/users/forms/DeleteUserForm';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting } from 'devextreme-react/data-grid';
import { Roles } from '@/core/types';
import useAuth from '@/shared/context/auth-context';

const LeviantAdminUsersPage: FunctionComponent = (): ReactElement => {
    const queryClient = useQueryClient();
    const { user: currentUser, isSpoofing } = useAuth();

    const [users, setUsers] = useState<CustomerUser[]>([]);

    const { queryKey: useUsersQueryKey, query: useUsersQuery } = useUsers({
        queryParams: { limit: 99999 },
    });

    const { data: roles } = useRoles();
    const { mutate: spoofUser, isLoading: isSpoofing_ } = useSpoofUser();

    const { data: usersPages, isLoading } = useUsersQuery;


    useEffect(() => {
        if (usersPages) {
            setUsers(usersPages.pages[0].data);
        }
    }, [usersPages]);

    // User form
    const [userForm, setUserForm] = useState<{
        open: boolean;
        formModel: UserFormModel;
        user?: CustomerUser;
    }>({
        open: false,
        formModel: initialUserFormModel,
    });

    // Delete form
    const [deleteUserForm, setDeleteUserForm] = useState<{ open: boolean; model?: CustomerUser }>({
        open: false,
        model: undefined,
    });

    const refetch = () => {
        /* eslint-disable @typescript-eslint/no-floating-promises */
        queryClient.invalidateQueries(useUsersQueryKey);
    };

    const isUserFormDisabled = useMemo(() => {
        const { firstName, lastName, email } = userForm.formModel;
        return !firstName.length || !lastName.length || !email || !email.length;
    }, [userForm.formModel]);

    const handleEdit = (user: CustomerUser) => {
        setUserForm({
            user,
            formModel: userFormModelFromUser(user),
            open: true,
        });
    };

    const handleDelete = (user: CustomerUser) => {
        setDeleteUserForm({ model: user, open: true });
    };

    const handleSpoof = (user: CustomerUser) => {
        spoofUser(user.id);
    };

    const renderActionsCell = (cellData: { data: CustomerUser }) => {
        const user = cellData.data;
        const canSpoof = !isSpoofing && user.id !== currentUser?.id;
        
        return (
            <Stack direction="row" spacing={1}>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(user);
                    }}
                >
                    <EditRounded fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(user);
                    }}
                >
                    <DeleteRounded fontSize="small" />
                </IconButton>
                {canSpoof && (
                    <Tooltip title="Spoof User">
                        <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSpoof(user);
                            }}
                            disabled={isSpoofing_}
                        >
                            <PersonOffRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const getRoleName = (roleId: string) => {
        const role = roles?.find((r) => r.id === roleId);
        return role?.title || '';
    };

    const isLeviantAdmin = (roleId: string) => {
        return getRoleName(roleId) === Roles.LEVIANT_ADMIN;
    };

    return (
        <AdminPanelPage
            title="User management"
            headerControls={
                <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    size="large"
                    onClick={() =>
                        setUserForm({ user: undefined, formModel: initialUserFormModel, open: true })
                    }
                >
                    Create User
                </Button>
            }
        >
            {isLoading ? (
                <Backdrop />
            ) : (
                <Box component={Paper} p={3}>
                    <Stack direction="column" spacing={3}>
                        <DataGrid
                            dataSource={users}
                            showBorders={true}
                            columnAutoWidth={true}
                            rowAlternationEnabled={true}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                        >
                            <Sorting mode="multiple" />
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />
                            <SearchPanel visible={true} highlightCaseSensitive={true} />
                            <Paging defaultPageSize={50} enabled={true} />

                            <Column dataField="firstName" caption="First Name" />
                            <Column dataField="lastName" caption="Last Name" />
                            <Column dataField="email" caption="Email" />
                            <Column
                                dataField="customer.name"
                                caption="Customer"
                                calculateCellValue={(rowData: CustomerUser) =>
                                    isLeviantAdmin(rowData.roleId) ? '' : rowData.customer?.name || ''
                                }
                            />
                            <Column
                                dataField="sites"
                                caption="Site"
                                calculateCellValue={(rowData: CustomerUser) =>
                                    isLeviantAdmin(rowData.roleId) ? '' : rowData.sites?.map((s) => s.name).join(', ') || ''
                                }
                            />
                            <Column
                                dataField="roleId"
                                caption="Role"
                                calculateCellValue={(rowData: CustomerUser) => getRoleName(rowData.roleId)}
                            />
                            <Column
                                dataField="pin"
                                caption="Pin"
                                alignment="right"
                                calculateCellValue={(rowData: CustomerUser) =>
                                    isLeviantAdmin(rowData.roleId) ? '' : rowData.pin || ''
                                }
                            />
                            <Column
                                caption="Actions"
                                width={120}
                                allowFiltering={false}
                                allowSorting={false}
                                allowReordering={false}
                                cellRender={renderActionsCell}
                            />
                        </DataGrid>
                    </Stack>
                </Box>
            )}
            <UserForm
                open={userForm.open}
                onClose={() => setUserForm({ ...userForm, open: false })}
                formModel={userForm.formModel}
                updateFormModel={(value: UserFormModel) => setUserForm({ ...userForm, formModel: value })}
                isAdminForm={true}
                disabled={isUserFormDisabled}
                user={userForm.user}
                onSubmitted={refetch}
            />
            <DeleteUserForm
                open={deleteUserForm.open}
                onClose={() => setDeleteUserForm({ ...deleteUserForm, open: false })}
                user={deleteUserForm.model}
                onSubmitted={refetch}
                isAdminForm={true}
            />
        </AdminPanelPage>
    );
};

export default LeviantAdminUsersPage;
